import { ClinicMark } from '@/components/ClinicMark'
import { useEffect, useRef, useState, type Ref } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '@/components/Icon'
import { PatientResultRow } from '@/features/search/PatientResultRow'
import { MIN_QUERY_LENGTH, useSearch } from '@/features/search/useSearch'
import { useMediaQuery } from '@/lib/useMediaQuery'
import { useModals } from '@/app/modals/ModalContext'

/** One search field on every screen: finding someone happens mid-consultation
 *  as often as at the start, so results drop under it rather than replacing the
 *  page.
 *
 *  And nothing else. The bar used to end in a bell and an avatar: the bell was
 *  wired to nothing, and the avatar was a third route to Settings behind the
 *  rail's own Settings row and the user chip above it. Both were shape rather
 *  than function, and they sat in the corner the eye checks first. */
export function TopBar({
  navOpen = false,
  menuButtonRef,
  onToggleNav,
}: {
  navOpen?: boolean
  menuButtonRef?: Ref<HTMLButtonElement>
  onToggleNav?: () => void
}) {
  const modals = useModals()
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const { enabled, hits, isLoading } = useSearch(text)
  // The full prompt is 38 characters. On a 393px phone it truncated mid-word to
  // "Search by (", which reads as a broken field rather than a shortened one.
  const narrow = useMediaQuery('(max-width: 30rem)')

  // Clicking anywhere else closes the results without clearing the query, so
  // the doctor can come back to it.
  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [])

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null
      const typing =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable
      if (event.key === '/' && !typing) {
        event.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
      }
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <header className="sticky top-0 z-header flex h-topbar flex-none items-center gap-3 border-b border-line-soft bg-surface px-3 lg:gap-4 lg:px-5">
      {/* Only below the rail breakpoint: above it the navigation is always on
          screen and a button to reveal it would do nothing. */}
      <button
        type="button"
        ref={menuButtonRef}
        className="grid size-touch flex-none place-items-center rounded-md text-fg-muted transition-colors duration-150 ease-standard hover:bg-hover hover:text-fg lg:hidden"
        aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
        aria-expanded={navOpen}
        aria-controls="main-nav"
        onClick={onToggleNav}
      >
        <Icon name={navOpen ? 'close' : 'menu'} />
      </button>

      <Link
        to="/"
        className="flex min-h-touch flex-none items-center gap-2 text-fg no-underline lg:hidden"
        aria-label="Casemark, home"
      >
        <ClinicMark className="size-8" />
        <span className="text-base font-bold tracking-snug max-[26rem]:hidden">Casemark</span>
      </Link>

      <div className="group relative flex min-w-0 max-w-search flex-1 items-center" ref={wrapRef}>
        <span className="pointer-events-none absolute left-3 grid place-items-center text-fg-subtle transition-colors duration-150 ease-standard group-focus-within:text-accent">
          <Icon name="search" />
        </span>
        <input
          ref={inputRef}
          className="search-plain h-field w-full rounded-md border border-transparent bg-subtle pr-touch pl-[calc(var(--spacing)*3+1.25rem+var(--spacing)*2)] text-fg transition duration-150 ease-standard placeholder:text-fg-subtle hover:bg-pressed focus:border-accent focus:bg-surface focus:ring-3 focus:ring-focus/20 focus:outline-none"
          type="search"
          placeholder={narrow ? 'Search patients…' : 'Search by OP No, name, phone number…'}
          aria-label="Search by OP number, name or phone number"
          value={text}
          onChange={(event) => {
            setText(event.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
          enterKeyHint="search"
        />
        {!text && (
          <kbd className="pointer-events-none absolute right-3 hidden h-5.5 items-center rounded-sm border border-line bg-surface px-2 font-mono text-2xs leading-none text-fg-subtle lg:flex group-focus-within:lg:hidden" aria-hidden="true">
            /
          </kbd>
        )}

        {text && (
          <button
            type="button"
            className="absolute right-0.5 grid size-touch place-items-center rounded-md text-fg-subtle hover:text-fg"
            aria-label="Clear search"
            onClick={() => {
              setText('')
              inputRef.current?.focus()
            }}
          >
            <Icon name="close" />
          </button>
        )}

        {open && enabled && (
          <ul className="absolute top-[calc(100%+var(--spacing)*2)] right-0 left-0 z-dropdown max-h-[60vh] list-none overflow-y-auto rounded-lg border border-line bg-raised p-1 shadow-lg [&>li+li]:border-t [&>li+li]:border-line-faint">
            {isLoading ? (
              <li className="p-4 text-sm text-fg-muted">Searching…</li>
            ) : hits.length === 0 ? (
              <li className="p-4 text-sm text-fg-muted">
                No patient found.{' '}
                <button
                  type="button"
                  className="bg-transparent p-0 text-accent underline underline-offset-2"
                  onClick={() => {
                    setOpen(false)
                    modals.open({ kind: 'patient', mode: 'create', seed: text.trim() })
                  }}
                >
                  Register this patient
                </button>
                .
              </li>
            ) : (
              hits.map((hit) => (
                <PatientResultRow
                  key={hit.id}
                  hit={hit}
                  onSelect={() => {
                    setOpen(false)
                    setText('')
                  }}
                />
              ))
            )}
          </ul>
        )}
        {open && text.trim().length > 0 && !enabled && (
          <ul className="absolute top-[calc(100%+var(--spacing)*2)] right-0 left-0 z-dropdown max-h-[60vh] list-none overflow-y-auto rounded-lg border border-line bg-raised p-1 shadow-lg [&>li+li]:border-t [&>li+li]:border-line-faint">
            <li className="p-4 text-sm text-fg-muted">
              Type at least {MIN_QUERY_LENGTH} characters.
            </li>
          </ul>
        )}
      </div>
    </header>
  )
}
